import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { pages } from '../data/pages';
import { useTheme } from '../components/ThemeProvider';

/**
 * Index — the landing page. Uses the dark "amber" palette from the original.
 *
 * Note: this page does NOT use the Layout component because the original
 * landing page is more typographically distinctive than the content pages.
 */
export function Index() {
  const { theme, toggle } = useTheme();
  useEffect(() => {
    document.documentElement.setAttribute('data-accent', 'index');
    document.title = 'Field Notes — ML &amp; Quant Research';
  }, []);

  return (
    <div className="paper-bg min-h-screen">
      {/* Site nav from the layout (rendered manually because Index is dark-themed by default) */}
      <nav
        aria-label="Site"
        className="no-print sticky top-0 z-50 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 bg-paper px-6 py-2.5 border-b border-rule font-mono text-[10px] uppercase tracking-[0.2em]"
      >
        <span className="text-accent font-semibold">Field Notes</span>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 list-none m-0 p-0">
          {pages.map((p) => (
            <li key={p.slug}>
              <Link to={`/${p.slug}`} title={p.title} className="text-ink-muted hover:text-accent">
                {p.tag}
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex gap-1.5">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="font-mono text-inherit border border-ink-muted text-ink-muted bg-transparent px-2 py-[3px] cursor-pointer hover:text-accent hover:border-accent"
          >
            ◐
          </button>
        </div>
      </nav>

      <main className="max-w-[720px] mx-auto px-8 pt-24 pb-32">
        <header className="border-b border-rule pb-10 mb-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent mb-4">
            Field Notes
          </div>
          <h1 className="font-display font-normal text-[clamp(36px,5vw,52px)] leading-[1.1] tracking-tight m-0 mb-5">
            Notes on the <em className="italic text-accent font-light">math &amp; methods</em> behind machine learning.
          </h1>
          <p className="font-display text-[19px] font-light text-ink-muted leading-snug max-w-[56ch] m-0">
            A small collection of cheatsheets and working notes — compact enough to carry, rigorous enough to trust.
          </p>
        </header>

        <ul className="list-none p-0 m-0 flex flex-col gap-0.5">
          {pages.map((p) => (
            <li key={p.slug}>
              <Link
                to={`/${p.slug}`}
                className="block px-1 py-6 border-b border-rule text-inherit no-underline transition-all hover:pl-3 hover:bg-gradient-to-r hover:from-accent/[0.04] hover:to-transparent"
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-[11px] tracking-wider text-ink-muted uppercase min-w-[28px]">
                    {p.tag}
                  </span>
                  <h2 className="font-display text-2xl font-normal tracking-tight leading-tight m-0">
                    {p.title}
                  </h2>
                  <span className="font-mono text-[11px] tracking-wider text-ink-muted uppercase ml-auto whitespace-nowrap">
                    {p.meta}
                  </span>
                </div>
                <p className="mt-1.5 ml-[46px] text-ink-muted text-[15px] max-w-[58ch]">
                  {p.desc}
                </p>
              </Link>
            </li>
          ))}
        </ul>

        <footer className="mt-20 pt-6 border-t border-rule font-mono text-[11px] tracking-wider uppercase text-ink-muted flex justify-between flex-wrap gap-4">
          <span>© Marouane BENALLA</span>
          <span>theme: {theme}</span>
        </footer>
      </main>
    </div>
  );
}
